import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { enqueueRpc } from "../../../lib/enqueueRpc";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { useScanPrefill } from "../../../hooks/useScanPrefill";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { ScanBox } from "../../../components/mobile/ScanBox";
import { FlowPhotoSlot } from "../../../components/mobile/FlowPhotoSlot";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

export default function RueckgabeFlow() {
    const flow = useFlowOperationId();
    const prefill = useScanPrefill();
    const [assetId, setAssetId] = useState("");
    const [comment, setComment] = useState("");
    const [photoId, setPhotoId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [scanRestartKey, setScanRestartKey] = useState(0);
    const [scanError, setScanError] = useState("");

    useEffect(() => {
        if (prefill?.assetId) {
            setAssetId(prefill.assetId);
            toast.success("Ausleihgegenstand aus Scan übernommen.");
        }
    }, [prefill]);

    const submit = async () => {
        if (!assetId) return toast.error("Ausleihgegenstand scannen oder Kennung eingeben.");
        setSubmitting(true);
        const res = await enqueueRpc({
            path: "/movements",
            rpcName: "Rückgabe",
            summary: `${assetId.slice(0, 8)}…`,
            payload: {
                client_operation_id: flow.clientOperationId,
                type: "return",
                asset_id: assetId,
                comment: comment || undefined,
                evidence_photo_id: photoId || undefined,
            },
        });
        setSubmitting(false);
        if (!res.ok) {
            if (res.retryable) toast.warning("Offline -- Rückgabe wartet in der Queue.");
            else return toast.error(res.error);
        } else {
            toast.success("Rückgabe gebucht.");
        }
        flow.rotate();
        setAssetId("");
        setComment("");
        setPhotoId(null);
        setScanRestartKey((key) => key + 1);
    };

    return (
        <FlowShell
            testId="flow-rueckgabe"
            title="Rückgabe"
            kind="Schnellflow"
            onSubmit={submit}
            submitDisabled={!assetId}
            submitting={submitting}
        >
            <ScanBox
                onResolved={(raw, resolved) => {
                    if (resolved?.kind === "asset") {
                        setAssetId(resolved.id);
                        setScanError("");
                        toast.success(`Ausleihgegenstand erkannt: ${resolved.label}`);
                    } else {
                        setAssetId("");
                        setScanError(`Code „${raw}“ ist keinem Ausleihgegenstand zugeordnet. Bitte erneut scannen oder die interne Kennung prüfen.`);
                    }
                }}
                restartKey={scanRestartKey}
                placeholder="Ausleihgegenstand scannen"
            />
            {scanError ? (
                <div role="alert" className="rounded-md border border-destructive/60 bg-destructive/10 p-3 text-sm text-foreground" data-testid="rueckgabe-scan-error">
                    {scanError}
                </div>
            ) : null}
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Interne Kennung
                </Label>
                <Input
                    value={assetId}
                    onChange={(e) => { setAssetId(e.target.value); setScanError(""); }}
                    className="h-12"
                    aria-label="Interne Kennung"
                    data-testid="rueckgabe-asset-id"
                />
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Zustand / Kommentar
                </Label>
                <Input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="ok / beschädigt / fehlend"
                    className="h-12"
                    aria-label="Zustand / Kommentar"
                    data-testid="rueckgabe-comment"
                />
            </div>
            <FlowPhotoSlot
                sessionKey={flow.clientOperationId}
                label="Rückgabe-Zustand"
                entityType="movement"
                purpose="return_condition"
                value={photoId}
                onChange={setPhotoId}
                helpText="Empfohlen bei Schaden -- wird an die Buchung geknüpft."
                testId="rueckgabe-photo"
            />
        </FlowShell>
    );
}
